import React, { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { euro } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { SelectCategoriaLavoro } from "@/components/SelectCategoriaLavoro";

const STATI = { aperta: "Aperta", in_corso: "In corso", conclusa: "Conclusa", fatturata: "Fatturata" };

const CAMPI = [
  { k: "materiali_stimati", label: "Acquisti stimati (€)" },
  { k: "ore_previste", label: "Ore previste" },
  { k: "costo_orario_medio", label: "Costo orario medio (€)" },
  { k: "trasferta_stimata", label: "Trasferte notti stimate (€)" },
  { k: "pasti_stimati", label: "Pasto cantiere stimato (€)" },
  { k: "cene_stimate", label: "Cena trasferta stimata (€)" },
  { k: "veicoli_stimati", label: "Veicoli stimati (€)" },
  { k: "peso_materiali_stimato_kg", label: "Materiale previsto (kg)" },
];

export function ModificaCommessa({ open, commessa, preventivo, onClose, onSaved }) {
  const [f, setF] = useState({});
  const { data: clienti = [] } = useQuery({ queryKey: ["clienti"], queryFn: async () => (await api.get("/clienti")).data, enabled: open });

  useEffect(() => {
    if (open && commessa) {
      setF({
        cliente_id: commessa.cliente_id || "",
        descrizione: commessa.descrizione || "",
        categoria_lavoro: commessa.categoria_lavoro || "",
        stato: commessa.stato || "aperta",
        importo_commessa: commessa.importo_commessa ?? 0,
        data_apertura: (commessa.data_apertura || "").slice(0, 10),
        data_inizio_lavori: commessa.data_inizio_lavori || "",
        data_fine_prevista: commessa.data_fine_prevista || "",
        giorni_preavviso_scadenza: commessa.giorni_preavviso_scadenza ?? 7,
        note: commessa.note || "",
        ...Object.fromEntries(CAMPI.map((c) => [c.k, preventivo?.[c.k] ?? commessa.preventivo?.[c.k] ?? 0])),
      });
    }
  }, [open, commessa, preventivo]);

  const mut = useMutation({
    mutationFn: async (p) => (await api.put(`/commesse/${commessa.id}`, p)).data,
    onSuccess: () => { toast.success("Commessa aggiornata"); onSaved(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const n = (k) => Number(f[k]) || 0;
  const totale = n("materiali_stimati") + n("ore_previste") * n("costo_orario_medio") + n("trasferta_stimata") + n("pasti_stimati") + n("cene_stimate") + n("veicoli_stimati");

  const salva = () => {
    if (!f.cliente_id || !f.descrizione?.trim()) return toast.error("Seleziona un cliente e inserisci una descrizione");
    if (!f.data_inizio_lavori || !f.data_fine_prevista) return toast.error("Inserisci Data inizio lavori e Data fine prevista");
    if (f.data_fine_prevista < f.data_inizio_lavori) return toast.error("La data fine prevista non può precedere la data inizio lavori");
    mut.mutate({
      cliente_id: f.cliente_id,
      descrizione: f.descrizione,
      categoria_lavoro: f.categoria_lavoro || "",
      stato: f.stato,
      importo_commessa: n("importo_commessa"),
      data_apertura: f.data_apertura || null,
      data_inizio_lavori: f.data_inizio_lavori,
      data_fine_prevista: f.data_fine_prevista,
      giorni_preavviso_scadenza: Math.max(0, parseInt(f.giorni_preavviso_scadenza, 10) || 0),
      note: f.note,
      preventivo: Object.fromEntries(CAMPI.map((c) => [c.k, n(c.k)])),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="modifica-commessa-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Modifica commessa</DialogTitle>
          <DialogDescription>Aggiorna i dati della commessa e le stime del preventivo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Cliente *</Label>
            <Select value={f.cliente_id || ""} onValueChange={(v) => set("cliente_id", v)}>
              <SelectTrigger data-testid="edit-commessa-cliente-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
              <SelectContent>{clienti.map((cl) => <SelectItem key={cl.id} value={cl.id}>{cl.ragione_sociale}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Descrizione del lavoro *</Label>
            <Textarea value={f.descrizione || ""} onChange={(e) => set("descrizione", e.target.value)} data-testid="edit-commessa-descrizione-input" />
            <Label className="mt-2 block">Categoria lavoro</Label>
            <SelectCategoriaLavoro value={f.categoria_lavoro} onChange={(v) => set("categoria_lavoro", v)} testid="edit-commessa-categoria-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={f.stato || "aperta"} onValueChange={(v) => set("stato", v)}>
                <SelectTrigger data-testid="edit-commessa-stato-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATI).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Importo fatturato (€)</Label>
              <Input type="number" min="0" step="0.01" value={f.importo_commessa ?? ""} onChange={(e) => set("importo_commessa", e.target.value)} data-testid="edit-commessa-importo-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Data apertura</Label>
              <Input type="date" value={f.data_apertura || ""} onChange={(e) => set("data_apertura", e.target.value)} data-testid="edit-commessa-apertura-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Giorni preavviso scadenza</Label>
              <Input type="number" min="0" step="1" value={f.giorni_preavviso_scadenza ?? ""} onChange={(e) => set("giorni_preavviso_scadenza", e.target.value)} data-testid="edit-commessa-preavviso-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Data inizio lavori *</Label>
              <Input type="date" value={f.data_inizio_lavori || ""} onChange={(e) => set("data_inizio_lavori", e.target.value)} data-testid="edit-commessa-inizio-lavori-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Data fine prevista *</Label>
              <Input type="date" value={f.data_fine_prevista || ""} min={f.data_inizio_lavori || undefined} onChange={(e) => set("data_fine_prevista", e.target.value)} data-testid="edit-commessa-fine-prevista-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Note</Label>
            <Textarea value={f.note || ""} onChange={(e) => set("note", e.target.value)} data-testid="edit-commessa-note-input" />
          </div>

          <div className="pt-2 border-t border-slate-100">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Preventivo</div>
            <div className="grid grid-cols-2 gap-3">
              {CAMPI.map((c) => (
                <div key={c.k} className="space-y-1.5">
                  <Label className="text-xs">{c.label}</Label>
                  <Input type="number" min="0" step="0.01" value={f[c.k] ?? ""} onChange={(e) => set(c.k, e.target.value)} data-testid={`prev-edit-${c.k}`} />
                </div>
              ))}
              <div className="col-span-2 flex items-center justify-between rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-sm">
                <span className="text-slate-600">Manodopera stimata: <b className="font-mono">{euro(n("ore_previste") * n("costo_orario_medio"))}</b></span>
                <span className="text-slate-600">Totale stimato: <b className="font-mono text-primary" data-testid="prev-edit-totale">{euro(totale)}</b></span>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="prev-edit-annulla">Annulla</Button>
          <Button onClick={salva} disabled={mut.isPending} data-testid="prev-edit-salva">{mut.isPending ? "Salvataggio…" : "Salva commessa"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
